// Utilitário para detecção facial e enquadramento automático padrão documento (3x4 / avatar).
// Enquadra a cabeça centralizada com margem superior e na altura dos ombros.

let modelosCarregados = false;
let carregandoPromessa = null;

function carregarScript(src) {
  if (typeof document === 'undefined') return Promise.resolve(false);
  return new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
}

async function obterFaceApi() {
  if (typeof window === 'undefined') return null;
  if (window.faceapi) return window.faceapi;

  // Tenta carregar do /models/face-api.js local
  const carregouLocal = await carregarScript('/models/face-api.js');
  if (carregouLocal && window.faceapi) return window.faceapi;

  // Fallback via CDN
  const carregouCdn = await carregarScript('https://cdn.jsdelivr.net/npm/@vladmandic/face-api/dist/face-api.js');
  if (carregouCdn && window.faceapi) return window.faceapi;

  return null;
}

export async function carregarModelosFace() {
  if (modelosCarregados) return true;
  if (carregandoPromessa) return carregandoPromessa;

  carregandoPromessa = (async () => {
    try {
      const faceapi = await obterFaceApi();
      if (!faceapi?.nets?.tinyFaceDetector) return false;
      // Carrega o modelo TinyFaceDetector local da pasta /models
      await faceapi.nets.tinyFaceDetector.loadFromUri('/models');
      modelosCarregados = true;
      return true;
    } catch {
      try {
        // Fallback para CDN oficial do modelo se a pasta local falhar
        const faceapi = await obterFaceApi();
        if (!faceapi?.nets?.tinyFaceDetector) return false;
        await faceapi.nets.tinyFaceDetector.loadFromUri('https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model');
        modelosCarregados = true;
        return true;
      } catch {
        return false;
      }
    } finally {
      carregandoPromessa = null;
    }
  })();

  return carregandoPromessa;
}

export async function detectarRosto(elemento) {
  // 1. Tenta a detecção nativa do navegador (Chromium/Edge/Android) via hardware, zero KB
  if (typeof window !== 'undefined' && 'FaceDetector' in window) {
    try {
      const detector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
      const faces = await detector.detect(elemento);
      if (faces && faces.length > 0) {
        const box = faces[0].boundingBox;
        return {
          x: box.x,
          y: box.y,
          width: box.width,
          height: box.height,
        };
      }
    } catch {
      // Segue para o face-api
    }
  }

  // 2. Fallback para TinyFaceDetector via face-api
  try {
    const faceapi = await obterFaceApi();
    if (!faceapi?.TinyFaceDetectorOptions) return null;

    const carregou = await carregarModelosFace();
    if (!carregou) return null;

    const options = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.35 });
    const detection = await faceapi.detectSingleFace(elemento, options);
    if (detection && detection.box) {
      return {
        x: detection.box.x,
        y: detection.box.y,
        width: detection.box.width,
        height: detection.box.height,
      };
    }
  } catch {
    // Silencioso
  }

  return null;
}

/**
 * Enquadra a foto com foco no rosto centralizado na altura dos ombros.
 * @param {HTMLImageElement|HTMLVideoElement|HTMLCanvasElement} fonte - Elemento visual de origem
 * @param {number} larguraOriginal - Largura real da fonte
 * @param {number} alturaOriginal - Altura real da fonte
 * @param {object} [opcoes] - Opções de redimensionamento
 * @returns {Promise<{ dataUrl: string, rostoDetectado: boolean }>}
 */
export async function enquadrarFotoDocumento(fonte, larguraOriginal, alturaOriginal, opcoes = {}) {
  const maxLado = opcoes.maxLado || 480;
  const qualidade = opcoes.qualidade || 0.85;

  let cropX = 0;
  let cropY = 0;
  let cropSize = Math.min(larguraOriginal, alturaOriginal);
  let rostoDetectado = false;

  try {
    const rosto = await detectarRosto(fonte);
    if (rosto && rosto.width > 0 && rosto.height > 0) {
      rostoDetectado = true;
      const centroX = rosto.x + rosto.width / 2;
      const centroY = rosto.y + rosto.height / 2;

      // Em fotos para documento (3x4/perfil):
      // - Cabeça ocupa ~50-55% da área total
      // - Margem acima da cabeça para respiro
      // - Base do corte desce na altura dos ombros/clavícula
      const escalaDocumento = 2.15;
      cropSize = Math.max(rosto.width * escalaDocumento, rosto.height * 1.85);

      // Limita ao tamanho máximo possível da imagem original
      if (cropSize > Math.min(larguraOriginal, alturaOriginal)) {
        cropSize = Math.min(larguraOriginal, alturaOriginal);
      }

      // Desloca o centro vertical do corte um pouco para baixo (12% do tamanho do corte)
      // para que a cabeça fique na parte superior e os ombros apareçam embaixo
      const cropCenterY = centroY + cropSize * 0.12;
      const cropCenterX = centroX;

      cropX = Math.round(cropCenterX - cropSize / 2);
      cropY = Math.round(cropCenterY - cropSize / 2);

      // Garante que o retângulo do corte não ultrapasse os limites da imagem original
      cropX = Math.max(0, Math.min(cropX, larguraOriginal - cropSize));
      cropY = Math.max(0, Math.min(cropY, alturaOriginal - cropSize));
    } else {
      // Se não detectou rosto, faz um crop quadrado centralizado padrão
      cropX = Math.round((larguraOriginal - cropSize) / 2);
      cropY = Math.round((alturaOriginal - cropSize) / 2);
    }
  } catch (err) {
    console.warn('Falha no enquadramento facial, usando recorte central padrão:', err);
    cropSize = Math.min(larguraOriginal, alturaOriginal);
    cropX = Math.round((larguraOriginal - cropSize) / 2);
    cropY = Math.round((alturaOriginal - cropSize) / 2);
  }

  const canvas = document.createElement('canvas');
  canvas.width = maxLado;
  canvas.height = maxLado;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(fonte, cropX, cropY, cropSize, cropSize, 0, 0, maxLado, maxLado);

  const dataUrl = canvas.toDataURL('image/jpeg', qualidade);
  return { dataUrl, rostoDetectado };
}
